import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Modal, Spin } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { all_routes } from "../../../router/all_routes";
import Table from "../../Common/dataTable/index";
import ActionsDropdown, { Action } from "../../Common/ActionsDropdown";
import useStudentManagement from "./hooks/useStudentManagement";
import { StudentType } from "../../../types/studentTypes";
import { GENDER_OPTIONS } from "../../../constants/userConstants";
import TableFilter, { FilterField } from "../../Common/TableFilter/TableFilter";
import StudentImportModal from "../../../feature-module/import/components/StudentImportModal";
import PageHeader from "components/Common/PageHeader";
import TableFilterDynamique from "components/Common/TableFilter/TableFilterDynamique";
import CustomButton from "components/Common/ui/CustomButton";
import { useAbility } from "config/abilityContext";

const { confirm } = Modal;

const StudentList = () => {
  const { t } = useTranslation();
  const ability = useAbility();
  const routes = all_routes;
  const {
    students,
    isLoading,
    isDeleting,
    handleDeleteStudent,
    fetchStudents,
    pagination
  } = useStudentManagement();

  const translatedGender = GENDER_OPTIONS.map(gender => ({
    ...gender,
    label: t(gender.label)
  }));

  const [filters, setFilters] = useState<{ [key: string]: string | number | null }>({});

  const filterFields: FilterField[] = [
    { key: "firstName", label: t("first_name"), type: "text" },
    { key: "lastName", label: t("last_name"), type: "text" },
    { key: "email", label: t("email"), type: "text" },
    { key: "class", label: t("class"), type: "text" },
    { key: "gender", label: t("gender"), type: "select", options: translatedGender },
  ];

  const handleFilterApply = (newFilters: { [key: string]: string | number | null }) => {
    setFilters(newFilters);
    fetchStudents({
      ...newFilters,
      page: 1,
      limit: pagination?.limit || 10,
    });
  };

  const handleFilterReset = () => {
    setFilters({});
    fetchStudents({
      page: 1,
      limit: pagination?.limit || 10,
    });
  };

  const handleTableChange = (pg: any) => {
    fetchStudents({
      ...filters,
      page: pg?.current || 1,
      limit: pg?.pageSize || 10,
    });
  };
  // useEffect(() => {
  //   fetchStudents({
  //     page: 1,
  //     limit: 10,
  //   });
  // }, []);

  const showDeleteConfirm = (studentId: string) => {
    confirm({
      title: t("are_you_sure_you_want_to_delete_this_student?"),
      icon: <ExclamationCircleOutlined />,
      content: t("this_action_cannot_be_undone"),
      okText: t("delete"),
      okType: "danger",
      cancelText: t("cancel"),
      okButtonProps: { disabled: isDeleting },
      onOk: () => handleDeleteStudent(studentId),
    });
  };

  const columns = [
    {
      title: t("first_name"),
      dataIndex: ["user", "firstName"],
      sorter: false,
    },
    {
      title: t("last_name"),
      dataIndex: ["user", "lastName"],
      sorter: false,
    },
    {
      title: t("email"),
      dataIndex: ["user", "email"],
      render: (_: any, record: StudentType) => (
        <Link
          to={ability.can("getAll", "Student") ?
            routes.studentDetails.replace(":id", record.id) + routes.studentDetailsTabs.details : "#"}
          className="link-primary"
        >
          {record.user.email}
        </Link>
      ),
      sorter: false,
    },
    {
      title: t("class"),
      dataIndex: ["class", "name"],
      render: (_: string, record: StudentType) => record.class?.name || "-",
      sorter: false,
    },
    {
      title: t("gender"),
      dataIndex: "gender",
      render: (_: string, record: StudentType) => {
        return translatedGender.find(option => option.value === record.user?.gender)?.label || "-";
      },
      sorter: false,
    },
    {
      title: t("massar_code"),
      dataIndex: "massarCode",
      render: (text: string) => text || "-",
      sorter: false,
    },
    {
      title: t("action"),
      dataIndex: "action",
      render: (_: string, record: StudentType) => {
        const actions: Action[] = [
          {
            label: t("details"),
            icon: "ti ti-eye",
            actionType: "link",
            to: routes.studentDetails.replace(":id", record.id) + routes.studentDetailsTabs.details,
            hidden: !ability.can("getAll", "Student"),
          },
          {
            label: t("edit"),
            icon: "ti ti-edit-circle",
            actionType: "link",
            to: routes.editStudent.replace(":id", record.id),
            hidden: !ability.can("update", "Student"),
          },
          {
            label: t("delete"),
            icon: "ti ti-trash-x",
            actionType: "modal",
            onClick: () => showDeleteConfirm(record.id),
            hidden: !ability.can("delete", "Student"),
          },
        ];
        return <ActionsDropdown actions={actions} />;
      },
    },
  ];

  const breadcrumbItems = [
    { label: t("dashboard"), path: all_routes.dashboard },
    { label: t("students"), active: true },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader
          title={t("students_list")}
          breadcrumbItems={breadcrumbItems}
        />

        <div className="d-flex mb-3 justify-content-end gap-2">
          {ability.can("create", "Student") && (
            <>
              <StudentImportModal onImportSuccess={fetchStudents} />
              <CustomButton
                to={routes.addStudent}
                variant="primary"
                size="md"
                disabled={false}
                onClick={() => { }}
                asLink
              >
                <i className="ti ti-square-rounded-plus me-2" />
                {t("add_student")}
              </CustomButton>
            </>
          )}

          <TableFilterDynamique
            fields={filterFields}
            filters={filters}
            onApply={handleFilterApply}
            onReset={handleFilterReset}
          />
        </div>

        <div className="table-responsive">
          {isLoading ? (
            <Spin size="large" className="d-flex justify-content-center my-5" />
          ) : (
            <Table
              dataSource={students}
              columns={columns}
              dataPaginate={{
                current: pagination?.page,
                pageSize: pagination?.limit,
                total: pagination?.total,
              }}
              pagination={true}
              onChange={handleTableChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentList;